import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRestaurantOutlined from '@mui/icons-material/TableRestaurantOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import { tableReportService } from '@/data/tableReportService';
import { TableReportBundle } from '@/types/TableReport.interface';
import { businessDayToday, getBranchTimezone } from '@/utils/businessDay';
import {
  formatCount,
  formatHourRange,
  formatMoney,
  presetRange,
  ReportPreset
} from '@/utils/salesReport';
import {
  formatMinutes,
  OCCUPANCY_BUCKETS,
  peakHoursInBusinessOrder
} from '@/utils/tableReport';
import ReportPresetBar, {
  ReportRangeValue
} from '@/components/salesReport/ReportPresetBar';
import {
  ReportCard,
  ReportEmpty,
  ReportStatPair
} from '@/components/salesReport/ReportParts';

interface TableReportsViewProps {
  /** Reports are per branch; null shows a "select a branch" note. */
  branchId: number | null;
}

const rangeFor = (
  preset: Exclude<ReportPreset, 'custom'>
): ReportRangeValue => ({
  preset,
  ...presetRange(preset, businessDayToday(getBranchTimezone()))
});

/** Horizontal count bars (peak hours, occupancy). */
const CountBars = ({
  rows,
  highlight
}: {
  rows: { key: string; label: string; count: number }[];
  highlight?: string | null;
}) => {
  const theme = useTheme();
  const max = Math.max(...rows.map((row) => row.count), 0);
  return (
    <Box>
      {rows.map((row) => {
        const color =
          row.key === highlight
            ? theme.palette.warning.main
            : theme.palette.primary.main;
        return (
          <Box
            key={row.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 110 }}
            >
              {row.label}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 10,
                borderRadius: 1,
                background: alpha(color, 0.15)
              }}
            >
              <Box
                sx={{
                  width: `${max > 0 ? (row.count / max) * 100 : 0}%`,
                  height: '100%',
                  borderRadius: 1,
                  background: color
                }}
              />
            </Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ minWidth: 36, textAlign: 'right' }}
            >
              {formatCount(row.count)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * "Masa & Garson" raporu: the six table reports the web POS shows, for one
 * branch, with the same date presets as the sales report.
 */
const TableReportsView = ({ branchId }: TableReportsViewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [range, setRange] = useState<ReportRangeValue>(() => rangeFor('today'));
  const [report, setReport] = useState<TableReportBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const generation = useRef(0);

  const fetchReport = useCallback(async () => {
    const current = ++generation.current;
    if (!branchId) {
      setReport(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const result = await tableReportService.getAll({
        branchId,
        startDate: range.startDate,
        endDate: range.endDate,
        timezone: getBranchTimezone()
      });
      if (current === generation.current) setReport(result);
    } catch (err) {
      console.error('Error fetching table reports:', err);
      if (current === generation.current) setError(true);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [branchId, range]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (!branchId) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {t('tableReports.selectBranch')}
      </Alert>
    );
  }

  const peak = report ? peakHoursInBusinessOrder(report.peakHours.hours) : [];
  const overview = report?.overview;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ReportPresetBar
          value={range}
          onPresetSelect={(preset) => setRange(rangeFor(preset))}
          onCustomApply={(startDate, endDate) =>
            setRange({ preset: 'custom', startDate, endDate })
          }
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, textTransform: 'none' }}
        >
          {t('tableReports.hint')}
        </Typography>
        {loading && report && <CircularProgress size={20} />}
        <Tooltip title={t('salesReport.refresh')}>
          <span>
            <IconButton
              color="primary"
              onClick={fetchReport}
              disabled={loading}
              aria-label={t('salesReport.refresh')}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchReport}>
              {t('salesReport.retry')}
            </Button>
          }
        >
          {t('tableReports.error')}
        </Alert>
      )}

      {!report && loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {report && overview && (
        <Grid
          container
          spacing={3}
          sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}
        >
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {[
                {
                  key: 'revenue',
                  label: t('tableReports.kpi.revenue'),
                  value: formatMoney(overview.totalRevenue),
                  sub: t('tableReports.kpi.closedChecks', {
                    n: formatCount(overview.closedChecks)
                  }),
                  color: theme.palette.primary.main
                },
                {
                  key: 'average',
                  label: t('tableReports.kpi.avgCheck'),
                  value: formatMoney(overview.avgCheckAmount),
                  sub: t('tableReports.kpi.openChecks', {
                    n: formatCount(overview.openChecks)
                  }),
                  color: theme.palette.success.main
                },
                {
                  key: 'duration',
                  label: t('tableReports.kpi.avgDuration'),
                  value: formatMinutes(overview.avgDurationMinutes),
                  sub: t('tableReports.kpi.perCheck'),
                  color: theme.palette.info.main
                },
                {
                  key: 'turnover',
                  label: t('tableReports.kpi.turnover'),
                  value: `${overview.turnoverRate.toLocaleString('tr-TR')}×`,
                  sub: t('tableReports.kpi.tables', {
                    n: formatCount(overview.totalTables)
                  }),
                  color: theme.palette.warning.main
                }
              ].map((card) => (
                <Grid item xs={12} sm={6} lg={3} key={card.key}>
                  <Card
                    sx={{
                      p: 2.5,
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha(card.color, 0.08)} 0%, ${alpha(card.color, 0.18)} 100%)`
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight="bold"
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      sx={{ color: card.color, my: 0.5 }}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.sub}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ReportCard
              title={t('tableReports.employees.title')}
              icon={<BadgeOutlined color="primary" />}
              footnote={t('tableReports.employees.hint')}
            >
              {report.employees.length === 0 ? (
                <ReportEmpty />
              ) : (
                <Table
                  size="small"
                  aria-label={t('tableReports.employees.title')}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tableReports.employees.name')}</TableCell>
                      <TableCell align="right">
                        {t('tableReports.col.checks')}
                      </TableCell>
                      <TableCell align="right">
                        {t('tableReports.col.revenue')}
                      </TableCell>
                      <TableCell align="right">
                        {t('tableReports.col.average')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.employees.map((row) => (
                      <TableRow key={row.employeeId}>
                        <TableCell>
                          {`${row.name} ${row.surname}`.trim()}
                        </TableCell>
                        <TableCell align="right">
                          {formatCount(row.checkCount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(row.revenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(row.avgCheckAmount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ReportCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ReportCard
              title={t('tableReports.tables.title')}
              icon={<TableRestaurantOutlined color="primary" />}
              footnote={t('tableReports.tables.hint')}
            >
              {report.tables.length === 0 ? (
                <ReportEmpty />
              ) : (
                <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                  <Table
                    size="small"
                    stickyHeader
                    aria-label={t('tableReports.tables.title')}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('tableReports.tables.table')}</TableCell>
                        <TableCell align="right">
                          {t('tableReports.col.checks')}
                        </TableCell>
                        <TableCell align="right">
                          {t('tableReports.col.revenue')}
                        </TableCell>
                        <TableCell align="right">
                          {t('tableReports.col.average')}
                        </TableCell>
                        <TableCell align="right">
                          {t('tableReports.col.duration')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.tables.map((row) => (
                        <TableRow key={row.tableId}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell align="right">
                            {formatCount(row.checkCount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(row.revenue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(row.avgCheckAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMinutes(row.avgDurationMinutes)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </ReportCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ReportCard
              title={t('tableReports.peak.title')}
              icon={<ScheduleOutlined color="primary" />}
              footnote={
                report.peakHours.peakHour !== null && peak.length > 0
                  ? t('tableReports.peak.busiest', {
                      range: formatHourRange(report.peakHours.peakHour)
                    })
                  : t('tableReports.peak.hint')
              }
            >
              {peak.length === 0 ? (
                <ReportEmpty />
              ) : (
                <CountBars
                  highlight={
                    report.peakHours.peakHour === null
                      ? null
                      : String(report.peakHours.peakHour)
                  }
                  rows={peak.map((bucket) => ({
                    key: String(bucket.hour),
                    label: formatHourRange(bucket.hour),
                    count: bucket.checkCount
                  }))}
                />
              )}
            </ReportCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ReportCard
              title={t('tableReports.occupancy.title')}
              icon={<TimerOutlined color="primary" />}
              footnote={t('tableReports.occupancy.hint', {
                average: formatMinutes(report.occupancy.avgDurationMinutes)
              })}
            >
              {report.occupancy.totalSessions === 0 ? (
                <ReportEmpty />
              ) : (
                <CountBars
                  rows={OCCUPANCY_BUCKETS.map((bucket) => ({
                    key: bucket.key,
                    label: t(bucket.labelKey),
                    count: report.occupancy.distribution[bucket.key]
                  }))}
                />
              )}
            </ReportCard>
          </Grid>

          <Grid item xs={12}>
            <ReportCard
              title={t('tableReports.waitlist.title')}
              icon={<HourglassEmptyOutlined color="primary" />}
              footnote={t('tableReports.waitlist.hint')}
            >
              {report.waitlist.total === 0 ? (
                <ReportEmpty />
              ) : (
                <ReportStatPair
                  items={[
                    {
                      label: t('tableReports.waitlist.total'),
                      value: formatCount(report.waitlist.total)
                    },
                    {
                      label: t('tableReports.waitlist.seated'),
                      value: formatCount(report.waitlist.seated),
                      color: theme.palette.success.main
                    },
                    {
                      label: t('tableReports.waitlist.noShow'),
                      value: `%${report.waitlist.noShowRate}`,
                      color: theme.palette.error.main
                    },
                    {
                      label: t('tableReports.waitlist.avgWait'),
                      value: formatMinutes(report.waitlist.avgWaitMinutes)
                    }
                  ]}
                />
              )}
            </ReportCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default TableReportsView;
